((root, factory) => {
  const model = factory();
  if (typeof module === "object" && module.exports) module.exports = model;
  else root.HbCartLoyaltyModel = model;
})(typeof window !== "undefined" ? window : globalThis, () => {
  const key = (value) =>
    String(value || "")
      .trim()
      .toLowerCase();
  const uniqueCodes = (codes) => [
    ...new Map(codes.filter(Boolean).map((code) => [key(code), String(code).trim()])).values(),
  ];
  const number = (value) => (value != null && value !== "" && Number.isFinite(Number(value)) ? Number(value) : null);
  const dateValue = (value) => (value && Number.isFinite(Date.parse(value)) ? Date.parse(value) : null);
  const formatters = new Map();
  const formatter = (kind, locale, currency) => {
    const cacheKey = JSON.stringify([kind, locale, currency]);
    if (!formatters.has(cacheKey)) {
      const value =
        kind === "money"
          ? new Intl.NumberFormat(locale, {
              style: "currency",
              currency,
              currencyDisplay: "code",
            })
          : new Intl.DateTimeFormat(locale, {
              day: "numeric",
              month: "short",
              year: "numeric",
            });
      if (formatters.size >= 32) formatters.delete(formatters.keys().next().value);
      formatters.set(cacheKey, value);
    }
    return formatters.get(cacheKey);
  };
  const formatMoney = (value, currency, locale = "en") => {
    if (number(value) === null || !/^[A-Z]{3}$/.test(currency || "")) return "";
    try {
      return formatter("money", locale, currency).format(Number(value));
    } catch {
      return `${currency} ${Number(value).toFixed(2)}`;
    }
  };
  const formatDate = (value, locale = "en") => {
    if (!dateValue(value)) return "";
    return formatter("date", locale).format(new Date(value));
  };
  // Okendo does not publish the redemption rule `type` enum. Keep unknown types (the POC rendered
  // every rule live) and drop only rewards this cart UI cannot apply as a single coupon code.
  const unsupportedRule = /shipping|product|gift|variable/;
  const normalizeRules = (payload = {}) =>
    (payload.redemptionRules || [])
      .filter(
        (rule) =>
          rule.redemptionRuleId &&
          !rule.isLimitReached &&
          rule.isActive !== false &&
          ![rule.type, rule.reward?.type].some((type) => unsupportedRule.test(key(type))) &&
          [rule.awardVia, rule.reward?.awardVia].every((delivery) => !key(delivery) || key(delivery) === "coupon"),
      )
      .map((rule) => ({
        ...rule,
        pointsCost: number(rule.reward?.points ?? rule.pointsCost),
        localisation: payload.localisation,
      }))
      .filter((rule) => rule.pointsCost > 0)
      .sort((first, second) => first.pointsCost - second.pointsCost);
  // Okendo's coupon statuses are "issued", "used", "expired" and "cancelled" (its storefront widget,
  // okendo-loyalty.onsite 0.1.0.1839, sorts coupons in that order). Only an issued coupon can still be used, so
  // anything else is left out, a status Okendo adds later too. A coupon without a status (the redemption
  // answer) is kept, and its expiry date still applies.
  const usableCouponStatuses = new Set(["", "issued", "active", "available"]);
  const normalizeCoupons = (payload = {}, now = Date.now()) => {
    if (!Array.isArray(payload.coupons)) throw new Error("Invalid active-coupon response");
    const coupons = payload.coupons
      .filter((coupon) => coupon && coupon.code)
      .filter((coupon) => {
        const expiry = coupon.expiryDate || coupon.expiresAt || coupon.expires_at;
        return (
          !coupon.isUsed &&
          !coupon.used &&
          !coupon.usedAt &&
          !coupon.dateUsed &&
          !coupon.revokedAt &&
          coupon.isActive !== false &&
          usableCouponStatuses.has(key(coupon.status)) &&
          (!expiry || (dateValue(expiry) !== null && dateValue(expiry) > now))
        );
      })
      .map((coupon) => ({
        ...coupon,
        code: String(coupon.code).trim(),
        expiresAt: coupon.expiryDate || coupon.expiresAt || coupon.expires_at || null,
        date: coupon.createdAt || coupon.date || null,
        ruleId: coupon.redemptionRuleId || coupon.ruleId || null,
      }));
    return [...new Map(coupons.map((coupon) => [key(coupon.code), coupon])).values()].sort(
      (first, second) =>
        (dateValue(first.expiresAt) ?? Infinity) - (dateValue(second.expiresAt) ?? Infinity) ||
        (dateValue(first.date) ?? Infinity) - (dateValue(second.date) ?? Infinity) ||
        key(first.code).localeCompare(key(second.code)),
    );
  };
  // Shoppers see the storefront's own "Save" label when a caller leaves it out.
  const defaultSave = () => (typeof window !== "undefined" && window.variantStrings?.save) || "Save";
  const rewardLabel = (rule, currency, locale, save = defaultSave()) => {
    const local = rule.reward?.localised;
    const localCurrency = rule.localisation?.currency?.code;
    if ([rule.type, rule.reward?.type].some((type) => key(type).includes("percent"))) {
      const value = number(rule.reward?.value ?? local?.value);
      if (value !== null) return `${save} ${value}%`;
      if (local?.formattedValue && String(local.formattedValue).includes("%")) return `${save} ${local.formattedValue}`;
      return String(rule.title || rule.reward?.title || "");
    }
    if (localCurrency === currency && number(local?.value) !== null)
      return `${save} ${formatMoney(local.value, currency, locale).replace(/\u00a0/g, " ")}`;
    if (localCurrency === currency && local?.formattedValue) {
      const value = String(local.formattedValue);
      return `${save} ${value.includes(currency) ? value : `${currency} ${value}`}`;
    }
    return String(rule.title || rule.reward?.title || "");
  };
  const couponLabel = (coupon, rules, currency, locale, save = defaultSave(), fallback = "Reward coupon", context = {}) => {
    const local = coupon.localised || coupon.localisation;
    const localCurrency = local?.currencyCode || local?.currency?.code;
    const value = number(coupon.value);
    const type = key(coupon.valueType || coupon.displayType || coupon.type);
    if (type.includes("percent")) {
      const percentage = value ?? number(local?.value);
      if (percentage !== null) return `${save} ${percentage}%`;
      return String(coupon.description || coupon.title || fallback);
    }
    if (localCurrency === currency && local?.formattedValue) return `${save} ${local.formattedValue}`;
    // Native fixed_amount discount values use the subscriber/program currency in major units.
    // Infer it only from explicit native context; other payloads retain their own labels.
    const nativeCurrency =
      key(coupon.valueType) === "fixed_amount" && key(coupon.displayType) === "discount"
        ? context.nativeCurrencyCode
        : null;
    const sourceCurrency = coupon.currencyCode || coupon.currency || nativeCurrency;
    if (value !== null && typeof sourceCurrency === "string" && /^[A-Z]{3}$/.test(sourceCurrency)) {
      const localisation = rules.find((r) => r.localisation?.currency?.code === currency)?.localisation;
      const rate = number(localisation?.currency?.exchangeRate);
      if (sourceCurrency === currency) return `${save} ${formatMoney(value, currency, locale)}`;
      if (sourceCurrency === localisation?.baseCurrencyCode && rate > 0)
        return `${save} ${formatMoney(value * rate, currency, locale)}`;
      return `${save} ${formatMoney(value, sourceCurrency, locale)}`;
    }
    const rule = rules.find((r) => key(r.redemptionRuleId) === key(coupon.ruleId));
    if (rule) return rewardLabel(rule, currency, locale, save) || fallback;
    return String(coupon.description || coupon.title || fallback);
  };
  const allocations = (cart) => {
    const result = [];
    for (const application of cart?.cart_level_discount_applications || []) {
      if (application.type === "discount_code" && Number(application.total_allocated_amount) > 0)
        result.push({
          code: application.code || application.title,
          amount: Number(application.total_allocated_amount),
        });
    }
    for (const item of cart?.items || [])
      for (const allocation of item.line_level_discount_allocations || []) {
        const application = allocation.discount_application || {};
        if (application.type === "discount_code" && Number(allocation.amount) > 0)
          result.push({
            code: application.code || application.title,
            amount: Number(allocation.amount),
          });
      }
    return result;
  };
  const appliedCodes = (cart) => uniqueCodes(allocations(cart).map((first) => first.code));
  const requestedCodes = (cart) =>
    uniqueCodes([
      ...appliedCodes(cart),
      ...(cart?.discount_codes || [])
        .filter((coupon) => typeof coupon === "string" || coupon.applicable !== false)
        .map((coupon) => coupon.code || coupon),
    ]);
  const replaceLoyaltyCode = (cart, coupons, nextCode) => {
    const known = new Set(
      [...coupons.map((coupon) => coupon.code), cart?.attributes?.loyalty_reward_code].filter(Boolean).map(key),
    );
    return uniqueCodes([...requestedCodes(cart).filter((coupon) => !known.has(key(coupon))), nextCode]);
  };
  // A points coupon carries the fixed value of one of the redemption rules. Those Shopify codes have no
  // minimum and cover every item (Admin, 25 Oct 2026); other Okendo vouchers ($5 off $100+ on a
  // collection) can be refused only because the cart doesn't qualify yet.
  const isPointsRuleCoupon = (coupon, rules = []) => {
    const value = number(coupon?.value);
    return (
      value !== null &&
      key(coupon.valueType).includes("fixed") &&
      rules.some((rule) => key(rule.reward?.type).includes("fixed") && number(rule.reward?.value) === value)
    );
  };
  // Shopify keeps every code it is sent on the cart, one that is used up, expired or deleted too, as
  // `applicable: false`: the same answer a live code gets when another discount beats it (live cart API,
  // 25 Oct 2026). A points coupon can only lose to another discount, so refused on a cart with a total
  // and no other discount, Shopify no longer accepts it. A code missing from the cart is dead either way.
  const isDeadCouponRefusal = (cart, code, pointsCoupon = true) => {
    if (!Array.isArray(cart?.discount_codes)) return false;
    const target = key(code);
    const entry = cart.discount_codes.find((coupon) => key(coupon?.code ?? coupon) === target);
    if (!entry) return true;
    if (!pointsCoupon) return false;
    if (typeof entry === "string" || entry.applicable !== false || !(Number(cart.total_price) > 0)) return false;
    const otherCode = cart.discount_codes.some(
      (coupon) => key(coupon?.code ?? coupon) !== target && (typeof coupon === "string" || coupon.applicable !== false),
    );
    const otherDiscount = [
      ...(cart.cart_level_discount_applications || []),
      ...(cart.items || []).flatMap((item) =>
        (item.line_level_discount_allocations || []).map((allocation) => allocation.discount_application || {}),
      ),
    ].some((application) => key(application.code || application.title) !== target);
    return !otherCode && !otherDiscount;
  };
  // Shopify's reasons for refusing a code that can never apply again: deleted, ended or expired (also when staff
  // end it in Shopify admin, which Okendo never hears about), or used up. Any other reason (no items, a minimum,
  // a customer-only code on a cart with no customer) belongs to a code that still works.
  const deadCouponWarnings = new Set([
    "DISCOUNT_NOT_FOUND",
    "DISCOUNT_CURRENTLY_INACTIVE",
    "DISCOUNT_USAGE_LIMIT_REACHED",
  ]);
  // What a coupon check says about one coupon (HbOkendoLoyalty.checkCoupons: an empty Storefront API cart with
  // only that code, its cartCreate answer): "dead" when Shopify refuses it for a reason above, "live" when it
  // keeps the code with no such reason (live API, 29 Oct 2026), "unknown" when there is no exact answer for it.
  const couponCheckVerdict = (result, code) => {
    const listed = (result?.cart?.discountCodes || []).some((entry) => key(entry?.code) === key(code));
    if (!listed || !Array.isArray(result.warnings)) return "unknown";
    return result.warnings.some((warning) => deadCouponWarnings.has(warning?.code)) ? "dead" : "live";
  };
  const discountBreakdown = (cart, rewardCode) => {
    const total = Math.max(0, Number(cart.total_discount) || 0);
    const loyalty = Math.min(
      total,
      allocations(cart)
        .filter((first) => key(first.code) === key(rewardCode))
        .reduce((sum, first) => sum + first.amount, 0),
    );
    return {
      loyalty,
      other: total - loyalty,
      total: Number(cart.total_price) || 0,
    };
  };
  return {
    key,
    uniqueCodes,
    number,
    dateValue,
    formatMoney,
    formatDate,
    normalizeRules,
    normalizeCoupons,
    rewardLabel,
    couponLabel,
    allocations,
    appliedCodes,
    requestedCodes,
    replaceLoyaltyCode,
    isPointsRuleCoupon,
    isDeadCouponRefusal,
    couponCheckVerdict,
    discountBreakdown,
  };
});
